import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useEditMapping } from "../api/mappingVersion";
import { buildEditMappingPayload, resolveDuplicateVersionNameError } from "../helpers/mappingVersion.helpers";
import { EditMappingFormValues, EditMappingResult, EditMode } from "../types/mappingVersion.types";
import { useInvalidateVersionCaches } from "./useInvalidateVersionCaches";
import { useNextVersionInfo } from "./useNextVersionInfo";

interface UseEditMappingFormArgs {
  open: boolean;
  drawingToolId: string | undefined;
  ownerId: string;
  sourceId: string;
  mappingType: number;
  onSuccess: (result: EditMappingResult) => void;
  /**
   * Whether "Create a new version" may be picked (the default version must be calculated --
   * see `canCreateNewVersion` in `versioningHelpers`). Defaults to allowed so existing callers
   * that don't pass it are unaffected.
   */
  canCreateNewVersion?: boolean;
}

export function useEditMappingForm({
  open,
  drawingToolId,
  ownerId,
  sourceId,
  mappingType,
  onSuccess,
  canCreateNewVersion = true
}: UseEditMappingFormArgs) {
  const invalidateVersionCaches = useInvalidateVersionCaches();
  const {
    nextVersionNumber,
    suggestedVersionName,
    existingVersionNames,
    isLoading: isLoadingNextVersionInfo
  } = useNextVersionInfo(drawingToolId);

  const form = useForm<EditMappingFormValues>({
    mode: "all",
    defaultValues: {
      reason: "",
      mode: EditMode.Update,
      versionName: ""
    }
  });

  const mode = form.watch("mode");
  const versionName = form.watch("versionName");

  useEffect(() => {
    if (open) {
      form.reset({ reason: "", mode: EditMode.Update, versionName: suggestedVersionName });
    }
    // Only re-seed when the dialog transitions to open, not on every suggestedVersionName refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (mode === EditMode.NewVersion && !versionName) {
      form.setValue("versionName", suggestedVersionName, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, suggestedVersionName]);

  useEffect(() => {
    // Safety net for the disabled radio option: covers the case where the default version's
    // calculated state resolves (or flips) to false after the user already picked "new-version".
    if (!canCreateNewVersion && mode === EditMode.NewVersion) {
      form.setValue("mode", EditMode.Update, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCreateNewVersion, mode]);

  const { mutate, isLoading: isSubmitting } = useEditMapping();

  const onSubmit = form.handleSubmit(values => {
    if (!drawingToolId) {
      return;
    }

    const payload = buildEditMappingPayload({ drawingToolId, ownerId, sourceId, mappingType, values });

    mutate(payload, {
      onSuccess: result => {
        // Both `update` and `new-version` share this call: `update` has no version-shaped side
        // effects today, but keeping the branches identical means the header stays correct even
        // if that changes later, at the cost of a couple of refetches that return the same data.
        invalidateVersionCaches(drawingToolId);
        onSuccess(result);
      },
      onError: error => {
        const duplicateNameMessage = resolveDuplicateVersionNameError(error);
        if (duplicateNameMessage) {
          form.setError("versionName", { type: "manual", message: duplicateNameMessage });
        }
      }
    });
  });

  return {
    form,
    mode,
    nextVersionNumber,
    existingVersionNames,
    isLoadingNextVersionInfo,
    isSubmitting,
    canCreateNewVersion,
    onSubmit
  };
}
